import { useEffect, useRef, useState,  useCallback } from "react";
import useShortenedUrls from "../../hooks/useShortenedUrls";
import useQRCodeDownloader from "../../hooks/useQRCodeDownloader";
import usePagination from "../../hooks/usePagination";
import useURLSearch from "../../hooks/useURLSearch";

import SearchBar from "../shared/SearchBar";
import URLEntry from "../url/URLEntry";
import Pagination from "../shared/Pagination";
import EmptyState from "../shared/EmptyState";
import { motion, AnimatePresence } from "framer-motion";
import Loading from "../shared/Loading";

import "../../styles/MyURLsPage.css";
import useURLActions from "../../hooks/useURLActions";
import useURLRedirect from "../../hooks/useURLRedirect";
import { useClientPrincipal } from "../../context/ClientPrincipalContext";
import { useUserId } from "../../hooks/useUserId";

const MyURLsPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResetKey, setSearchResetKey] = useState(0);
  const { clientPrincipal } = useClientPrincipal();
  const [isLoading, setIsLoading] = useState(true);

  const {
    shortenedUrls,
    setShortenedUrls,
    fetchShortenedUrls,
    updateFileFormat,
  } = useShortenedUrls();
  const isFetchedRef = useRef(false);

  const userId = useUserId();

    const loadUrls = useCallback(async () => {
    setIsLoading(true);
    await fetchShortenedUrls();
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (userId && !isFetchedRef.current) {
     loadUrls();
      isFetchedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, loadUrls]);

  const { handleDelete, handleCopy, handleToggleFavourite, handleUpdateUrl } =
    useURLActions(setShortenedUrls, clientPrincipal, fetchShortenedUrls);

  const { handleRedirect } = useURLRedirect();
  const { handleDownloadQRCode } = useQRCodeDownloader(shortenedUrls);

  const {
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    totalPages,
    startIdx,
    endIdx,
    paginatedEntries: paginatedUrls,
    totalItems,
  } = usePagination(Object.entries(shortenedUrls), 10, searchResetKey);

  const { handleSearch } = useURLSearch(setShortenedUrls, () =>
    setSearchResetKey((prev) => prev + 1),
  );

    if (isLoading) {
    return <Loading message="Loading URLs..." onRetry={loadUrls} />;
  }

  return (
    <div className="my-urls-container">
      <div className="shortened-list myurls-list-scroll">
        {Object.keys(shortenedUrls).length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <SearchBar
              title="My URLs"
              placeholder="Search by name or URL"
              searchQuery={searchQuery}
              onSearchQueryChange={(e) => setSearchQuery(e.target.value)}
              onSearch={() => handleSearch(searchQuery)}
            />
            <AnimatePresence>
              {paginatedUrls.map(
                ([shortCode, data]: [
                  string,
                  {
                    url: string;
                    name: string;
                    fileFormat: "png" | "svg";
                    prefix: string;
                    isFavourite: boolean;
                    expirationDate: string;
                    customColor?: string;
                    withLogo?: boolean;
                    logoId?: string;
                    totalScans?: number;
                  },
                ]) => {
                  const {
                    url,
                    name,
                    fileFormat,
                    prefix,
                    isFavourite,
                    expirationDate,
                    customColor,
                    withLogo,
                    totalScans,
                  } = data;
                  return (
                    <motion.div
                      key={shortCode}
                      initial={{ opacity: 0, y: 36 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 36 }}
                      transition={{ duration: 0.33, ease: "easeOut" }}
                      layout
                    >
                      <URLEntry
                        shortCode={shortCode}
                        url={url}
                        name={name}
                        expirationDate={expirationDate}
                        fileFormat={fileFormat}
                        prefix={prefix}
                        isFavourite={isFavourite}
                        customColor={customColor}
                        withLogo={withLogo}
                        logoId={data.logoId || "cgi"}
                        totalScans={totalScans}
                        onDelete={() => handleDelete(shortCode, name)}
                        onCopy={handleCopy}
                        onFileFormatChange={updateFileFormat}
                        onDownloadQRCode={handleDownloadQRCode}
                        onRedirect={handleRedirect}
                        onToggleFavourite={() =>
                          handleToggleFavourite(shortCode, isFavourite)
                        }
                        onUpdate={handleUpdateUrl}
                      />
                    </motion.div>
                  );
                },
              )}
            </AnimatePresence>
          </>
        )}
      </div>

      {Object.keys(shortenedUrls).length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          setCurrentPage={setCurrentPage}
          startIdx={startIdx}
          endIdx={endIdx}
          totalItems={totalItems}
        />
      )}
    </div>
  );
};
export default MyURLsPage;
